import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Tooltip,
  alpha,
  Paper,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Card,
  CardContent,
  Grid,
  TextField,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import BiotechIcon from '@mui/icons-material/Biotech';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import HomeIcon from '@mui/icons-material/Home';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import GrassIcon from '@mui/icons-material/Grass';
import BugReportIcon from '@mui/icons-material/BugReport';
import HealingIcon from '@mui/icons-material/Healing';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import ScienceIcon from '@mui/icons-material/Science';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import { toast } from 'react-hot-toast';
import { useAction } from 'convex/react';
import { useSessionAction } from '../../hooks/useSession';
import { api } from '../../convex/_generated/api';
import { usePlantAnalysis } from '../../hooks/useAI';
import { useAuth } from '../../contexts/AuthContext';
import SaveIcon from '@mui/icons-material/Save';

// Hidden input style
const HiddenInput = styled('input')({
  display: 'none',
});

const UploadArea = styled(Box)<{ hasImage: boolean }>(({ theme, hasImage }) => ({
  border: `2px dashed ${hasImage ? theme.palette.success.main : theme.palette.grey[400]}`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: hasImage
    ? alpha(theme.palette.success.main, 0.1)
    : alpha(theme.palette.grey[100], 0.5),
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    borderColor: theme.palette.primary.main,
  },
}));

// Styled button for header
export const PlantAnalyzerButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.common.white,
  backgroundColor: alpha(theme.palette.warning.main, 0.2),
  border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(1),
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.warning.main, 0.3),
    borderColor: theme.palette.warning.main,
    transform: 'scale(1.05)',
  },
  '&:active': {
    transform: 'scale(0.95)',
  },
}));

// Health status colors
const getHealthStatusColor = (status: string) => {
  switch (status) {
    case 'healthy': return 'success';
    case 'minor_issues': return 'info';
    case 'moderate_issues': return 'warning';
    case 'severe_issues': return 'error';
    case 'critical': return 'error';
    default: return 'default';
  }
};

const getHealthStatusText = (status: string) => {
  switch (status) {
    case 'healthy': return 'Egészséges';
    case 'minor_issues': return 'Kisebb problémák';
    case 'moderate_issues': return 'Közepes problémák';
    case 'severe_issues': return 'Súlyos problémák';
    case 'critical': return 'Kritikus állapot';
    default: return 'Ismeretlen';
  }
};

const getUrgencyColor = (urgency: string) => {
  switch (urgency) {
    case 'none': return 'success';
    case 'low': return 'info';
    case 'medium': return 'warning';
    case 'high': return 'error';
    case 'immediate': return 'error';
    default: return 'default';
  }
};

const getUrgencyText = (urgency: string) => {
  switch (urgency) {
    case 'none': return 'Nincs sürgős teendő';
    case 'low': return 'Alacsony prioritás';
    case 'medium': return 'Közepes prioritás';
    case 'high': return 'Magas prioritás';
    case 'immediate': return 'Azonnali beavatkozás szükséges!';
    default: return 'Ismeretlen';
  }
};

const getSeverityIcon = (severity: string) => {
  switch (severity) {
    case 'mild': return <InfoIcon color="info" />;
    case 'moderate': return <WarningAmberIcon color="warning" />;
    case 'severe': return <ErrorIcon color="error" />;
    default: return <InfoIcon />;
  }
};

const getIssueTypeIcon = (type: string) => {
  switch (type) {
    case 'disease': return <BugReportIcon />;
    case 'pest': return <BugReportIcon />;
    case 'deficiency': return <ScienceIcon />;
    case 'environmental': return <WbSunnyIcon />;
    case 'care_issue': return <HealingIcon />;
    default: return <InfoIcon />;
  }
};

const getIssueTypeText = (type: string) => {
  switch (type) {
    case 'disease': return 'Betegség';
    case 'pest': return 'Kártevő';
    case 'deficiency': return 'Hiánybetegség';
    case 'environmental': return 'Környezeti tényező';
    case 'care_issue': return 'Gondozási probléma';
    default: return 'Egyéb';
  }
};

// Types
interface PlantHealthAnalysis {
  plantIdentified: boolean;
  plantName?: {
    hungarian: string;
    latin: string;
  };
  healthStatus: string;
  healthScore: number;
  overallAssessment: string;
  identifiedIssues: Array<{
    name: string;
    type: string;
    severity: string;
    description: string;
    affectedParts: string[];
    symptoms: string[];
  }>;
  causes: Array<{
    issue: string;
    possibleCauses: string[];
    mostLikelyCause: string;
  }>;
  professionalSolutions: Array<{
    issue: string;
    treatment: string;
    products: string[];
    applicationMethod: string;
    frequency: string;
    expectedResults: string;
    warnings: string[];
  }>;
  homeSolutions: Array<{
    issue: string;
    treatment: string;
    ingredients: string[];
    preparation: string;
    applicationMethod: string;
    frequency: string;
    effectiveness: string;
    tips: string[];
  }>;
  preventionTips: string[];
  careRecommendations: {
    watering: string;
    light: string;
    temperature: string;
    humidity: string;
    fertilizing: string;
    soil: string;
    generalTips: string[];
  };
  urgency: string;
  prognosis: string;
}

interface PlantAnalyzerProps {
  onFinished?: () => void;
  externalOpen?: boolean;
  onExternalOpenChange?: (open: boolean) => void;
  dialogOnly?: boolean;
}

const PlantAnalyzer: React.FC<PlantAnalyzerProps> = ({
  onFinished,
  externalOpen,
  onExternalOpenChange,
  dialogOnly = false,
}) => {
  const { analyzePlantHealth, chatAboutAnalysis } = usePlantAnalysis();
  const { user } = useAuth();
  const saveHealthAnalysis = useSessionAction(
    api.plantHealthAnalysis.saveHealthAnalysisComplete
  );
  const storeBase64PlantImage = useSessionAction(api.ai.plantImageStorage.storeBase64PlantImage);

  // Dialog state
  const [internalDialogOpen, setInternalDialogOpen] = useState(false);
  const dialogOpen = externalOpen !== undefined ? externalOpen : internalDialogOpen;
  const setDialogOpen = (open: boolean) => {
    if (onExternalOpenChange) {
      onExternalOpenChange(open);
    }
    setInternalDialogOpen(open);
  };

  const [resultDialogOpen, setResultDialogOpen] = useState(false);

  // Image state
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  // Analysis state
  const [analysisResult, setAnalysisResult] = useState<PlantHealthAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Save to plant state
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<{
    status: 'success' | 'error';
    action?: 'updated' | 'created_pending';
    message: string;
  } | null>(null);

  // Plant name correction state
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [reanalyzing, setReanalyzing] = useState(false);

  // Follow-up chat state
  const [chatMessages, setChatMessages] = useState<
    { role: 'user' | 'assistant'; content: string }[]
  >([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  // Input refs
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle file selection
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadedFile(file);
    setAnalysisResult(null);
    setError(null);

    const reader = new FileReader();
    reader.onload = () => {
      setUploadedImage(reader.result as string);
    };
    reader.readAsDataURL(file);

    e.target.value = '';
  }, []);

  // Handle analyze - Convex action hívás base64 data URL-lel
  const handleAnalyze = async () => {
    if (!uploadedImage) {
      toast.error('Kérem töltsön fel egy képet!');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await analyzePlantHealth({ imageUrl: uploadedImage });

      if (response.success && response.analysis) {
        setAnalysisResult(response.analysis);
        setDialogOpen(false);
        setResultDialogOpen(true);
        toast.success('Elemzés elkészült!');
      } else {
        setError(response.error || 'Hiba történt az elemzés során');
        toast.error(response.error || 'Hiba történt az elemzés során');
      }
    } catch (err: any) {
      setError('Hiba történt az elemzés során');
      toast.error('Hiba történt az elemzés során');
      console.error('Analysis error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Save analysis to plant (Convex mutation + opcionális képmentés)
  const handleSaveToPlant = async () => {
    if (!analysisResult || !analysisResult.plantName?.latin) {
      toast.error('Nincs elmenthető elemzés vagy a növény nem azonosított');
      return;
    }

    setSaving(true);
    try {
      const response = await saveHealthAnalysis({
        analysis: analysisResult,
      });

      if (response.success) {
        // Új pending növénynél a feltöltött fotót is elmentjük (nem kritikus)
        if (
          response.action === 'created_pending' &&
          response.plantId &&
          uploadedImage?.startsWith('data:')
        ) {
          try {
            await storeBase64PlantImage({
              plantId: response.plantId,
              imageBase64: uploadedImage,
            });
          } catch (imgErr) {
            console.error('Képmentési hiba (nem kritikus):', imgErr);
          }
        }
        setSaveResult({
          status: 'success',
          action: response.action,
          message: response.message || 'Az elemzés sikeresen mentve!',
        });
        toast.success(response.message || 'Az elemzés sikeresen mentve!');
      } else {
        setSaveResult({
          status: 'error',
          message: response.error || 'Hiba történt a mentés során',
        });
        toast.error(response.error || 'Hiba történt a mentés során');
      }
    } catch (err: any) {
      setSaveResult({ status: 'error', message: 'Hiba történt a mentés során' });
      toast.error('Hiba történt a mentés során');
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  // Növénynév javítása → újraelemzés a megadott névvel
  const handleReanalyzeWithName = async () => {
    const name = nameInput.trim();
    if (!name) {
      toast.error('Add meg a növény nevét!');
      return;
    }
    if (!uploadedImage) {
      toast.error('Az eredeti kép már nem elérhető, kérlek töltsd fel újra.');
      return;
    }

    setReanalyzing(true);
    try {
      const response = await analyzePlantHealth({
        imageUrl: uploadedImage,
        plantNameHint: name,
      });

      if (response.success && response.analysis) {
        setAnalysisResult(response.analysis);
        setEditingName(false);
        setNameInput('');
        // Új elemzés → a korábbi mentési státusz és chat már nem érvényes
        setSaveResult(null);
        setChatMessages([]);
        toast.success('Újraelemzés kész a megadott névvel!');
      } else {
        toast.error(response.error || 'Hiba történt az újraelemzés során');
      }
    } catch (err: any) {
      toast.error('Hiba történt az újraelemzés során');
      console.error('Reanalyze error:', err);
    } finally {
      setReanalyzing(false);
    }
  };

  // Folytatólagos chat a növényorvossal
  const handleSendChat = async () => {
    const question = chatInput.trim();
    if (!question || chatLoading || !analysisResult) return;

    const newMessages = [
      ...chatMessages,
      { role: 'user' as const, content: question },
    ];
    setChatMessages(newMessages);
    setChatInput('');
    setChatLoading(true);

    try {
      const response = await chatAboutAnalysis({
        analysis: analysisResult,
        messages: newMessages,
      });

      if (response.success && response.reply) {
        setChatMessages([
          ...newMessages,
          { role: 'assistant', content: response.reply },
        ]);
      } else {
        toast.error(response.error || 'Hiba történt a válasz során');
        // A kérdést visszatesszük az inputba, hogy ne vesszen el
        setChatMessages(chatMessages);
        setChatInput(question);
      }
    } catch (err: any) {
      toast.error('Hiba történt a válasz során');
      console.error('Chat error:', err);
      setChatMessages(chatMessages);
      setChatInput(question);
    } finally {
      setChatLoading(false);
    }
  };

  // Reset state
  const reset = () => {
    setUploadedImage(null);
    setUploadedFile(null);
    setAnalysisResult(null);
    setError(null);
    setDialogOpen(false);
    setResultDialogOpen(false);
    setLoading(false);
    setSaving(false);
    setSaveResult(null);
    setEditingName(false);
    setNameInput('');
    setReanalyzing(false);
    setChatMessages([]);
    setChatInput('');
    setChatLoading(false);
  };

  const handleDialogClose = () => {
    if (!loading) {
      setDialogOpen(false);
    }
  };

  const handleResultDialogClose = () => {
    setResultDialogOpen(false);
    reset();
    onFinished?.();
  };

  return (
    <>
      {/* Hidden inputs */}
      <HiddenInput
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
      />
      <HiddenInput
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileChange}
      />

      {/* Button */}
      {!dialogOnly && (
        <Tooltip title="Növény Elemző (Növényorvos)" enterDelay={500}>
          <PlantAnalyzerButton
            onClick={() => setDialogOpen(true)}
            aria-label="Növény Elemző"
          >
            <BiotechIcon />
          </PlantAnalyzerButton>
        </Tooltip>
      )}

      {/* Upload Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BiotechIcon color="warning" />
          Növény Elemző - Növényorvos
          <IconButton
            onClick={handleDialogClose}
            disabled={loading}
            sx={{ ml: 'auto' }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Tölts fel egy képet a növényedről, és a mesterséges intelligencia kertészeti szakemberként és növényorvosként elemzi az állapotát, azonosítja a problémákat, és professzionális valamint házi megoldásokat javasol.
          </Alert>

          {/* Image upload area */}
          {uploadedImage ? (
            <Box>
              <UploadArea
                hasImage
                onClick={() => fileInputRef.current?.click()}
                sx={{ mb: 2 }}
              >
                <img
                  src={uploadedImage}
                  alt="uploaded"
                  style={{ maxWidth: '100%', maxHeight: 250, borderRadius: 8 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Kattintson új kép választásához
                </Typography>
              </UploadArea>
            </Box>
          ) : (
            <UploadArea
              hasImage={false}
              onClick={() => fileInputRef.current?.click()}
            >
              <BiotechIcon sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Kattintson kép kiválasztásához
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Támogatott formátumok: JPEG, PNG, WebP
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                vagy használja az alábbi gombokat
              </Typography>
            </UploadArea>
          )}

          {/* Camera and gallery buttons */}
          <Box display="flex" justifyContent="center" gap={2} sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<CameraAltIcon />}
              onClick={() => cameraInputRef.current?.click()}
              disabled={loading}
            >
              Fényképezés
            </Button>
            <Button
              variant="outlined"
              startIcon={<PhotoLibraryIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
            >
              Galéria
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={reset} disabled={loading} color="inherit">
            Mégse
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleAnalyze}
            disabled={loading || !uploadedFile}
            startIcon={loading ? <CircularProgress size={20} /> : <BiotechIcon />}
          >
            {loading ? 'Elemzés folyamatban...' : 'Növény Elemzése'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Results Dialog */}
      <Dialog
        open={resultDialogOpen}
        onClose={handleResultDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <BiotechIcon color="warning" />
          Növényorvosi Elemzés Eredménye
          <IconButton
            onClick={handleResultDialogClose}
            sx={{ ml: 'auto' }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {analysisResult && (
            <Box>
              {/* Header with plant info and health score */}
              <Paper elevation={0} sx={{ p: 3, backgroundColor: 'background.default' }}>
                <Grid container spacing={3} alignItems="center">
                  {/* Image preview */}
                  {uploadedImage && (
                    <Grid item xs={12} sm={4}>
                      <Box
                        component="img"
                        src={uploadedImage}
                        alt="Plant"
                        sx={{
                          width: '100%',
                          maxHeight: 200,
                          objectFit: 'cover',
                          borderRadius: 2,
                        }}
                      />
                    </Grid>
                  )}

                  {/* Plant info */}
                  <Grid item xs={12} sm={uploadedImage ? 8 : 12}>
                    {analysisResult.plantName && (
                      <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                        <Typography variant="h5">
                          {analysisResult.plantName.hungarian}
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                            sx={{ ml: 1, fontStyle: 'italic' }}
                          >
                            ({analysisResult.plantName.latin})
                          </Typography>
                        </Typography>
                        <Tooltip title="Nem ez a növény? Javítsd a nevet és elemezzük újra!">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditingName(!editingName);
                              setNameInput(analysisResult.plantName?.hungarian || '');
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}

                    {/* Névjavítás → újraelemzés */}
                    {editingName && (
                      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Add meg a növény helyes nevét — az elemzést újrageneráljuk
                          ezzel a névvel (a diagnózis is frissül).
                        </Typography>
                        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                          <TextField
                            size="small"
                            label="Növény neve"
                            value={nameInput}
                            onChange={(e) => setNameInput(e.target.value)}
                            disabled={reanalyzing}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleReanalyzeWithName();
                            }}
                            sx={{ flexGrow: 1, minWidth: 200 }}
                          />
                          <Button
                            variant="contained"
                            onClick={handleReanalyzeWithName}
                            disabled={reanalyzing || !nameInput.trim()}
                            startIcon={
                              reanalyzing ? <CircularProgress size={18} /> : <BiotechIcon />
                            }
                          >
                            {reanalyzing ? 'Újraelemzés...' : 'Újraelemzés'}
                          </Button>
                          <Button
                            variant="text"
                            onClick={() => setEditingName(false)}
                            disabled={reanalyzing}
                          >
                            Mégse
                          </Button>
                        </Box>
                      </Paper>
                    )}

                    {/* Health score */}
                    <Box sx={{ mb: 2 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Typography variant="body2" color="text.secondary">
                          Egészségi állapot:
                        </Typography>
                        <Chip
                          label={getHealthStatusText(analysisResult.healthStatus)}
                          color={getHealthStatusColor(analysisResult.healthStatus) as any}
                          size="small"
                        />
                        <Chip
                          label={`${analysisResult.healthScore}/100`}
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={analysisResult.healthScore}
                        color={getHealthStatusColor(analysisResult.healthStatus) as any}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>

                    {/* Urgency */}
                    {analysisResult.urgency && analysisResult.urgency !== 'none' && (
                      <Chip
                        icon={<WarningAmberIcon />}
                        label={getUrgencyText(analysisResult.urgency)}
                        color={getUrgencyColor(analysisResult.urgency) as any}
                        sx={{ mb: 2 }}
                      />
                    )}

                    {/* Overall assessment */}
                    <Typography
                      variant="body1"
                      paragraph
                      sx={{
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {analysisResult.overallAssessment}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Box sx={{ p: 2 }}>
                {/* Identified Issues */}
                {analysisResult.identifiedIssues && analysisResult.identifiedIssues.length > 0 && (
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <ErrorIcon color="error" />
                        <Typography variant="h6">
                          Azonosított Problémák ({analysisResult.identifiedIssues.length})
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {analysisResult.identifiedIssues.map((issue, index) => (
                        <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                          <CardContent>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              {getIssueTypeIcon(issue.type)}
                              <Typography variant="h6">{issue.name}</Typography>
                              <Chip
                                label={getIssueTypeText(issue.type)}
                                size="small"
                                variant="outlined"
                              />
                              {getSeverityIcon(issue.severity)}
                            </Box>
                            <Typography variant="body2" paragraph>
                              {issue.description}
                            </Typography>
                            {issue.symptoms && issue.symptoms.length > 0 && (
                              <Box mb={1}>
                                <Typography variant="subtitle2" color="text.secondary">
                                  Tünetek:
                                </Typography>
                                <Box display="flex" flexWrap="wrap" gap={0.5}>
                                  {issue.symptoms.map((symptom, i) => (
                                    <Chip key={i} label={symptom} size="small" variant="outlined" />
                                  ))}
                                </Box>
                              </Box>
                            )}
                            {issue.affectedParts && issue.affectedParts.length > 0 && (
                              <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                  Érintett részek:
                                </Typography>
                                <Box display="flex" flexWrap="wrap" gap={0.5}>
                                  {issue.affectedParts.map((part, i) => (
                                    <Chip key={i} label={part} size="small" color="warning" variant="outlined" />
                                  ))}
                                </Box>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Causes */}
                {analysisResult.causes && analysisResult.causes.length > 0 && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <InfoIcon color="info" />
                        <Typography variant="h6">Lehetséges Okok</Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {analysisResult.causes.map((cause, index) => (
                        <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                          <CardContent>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                              {cause.issue}
                            </Typography>
                            <Typography variant="body2" color="success.main" gutterBottom>
                              <strong>Legvalószínűbb ok:</strong> {cause.mostLikelyCause}
                            </Typography>
                            {cause.possibleCauses && cause.possibleCauses.length > 0 && (
                              <>
                                <Typography variant="subtitle2" color="text.secondary">
                                  További lehetséges okok:
                                </Typography>
                                <List dense>
                                  {cause.possibleCauses.map((pc, i) => (
                                    <ListItem key={i}>
                                      <ListItemText primary={pc} />
                                    </ListItem>
                                  ))}
                                </List>
                              </>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Professional Solutions */}
                {analysisResult.professionalSolutions && analysisResult.professionalSolutions.length > 0 && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LocalHospitalIcon color="primary" />
                        <Typography variant="h6">Professzionális Megoldások</Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {analysisResult.professionalSolutions.map((sol, index) => (
                        <Card key={index} variant="outlined" sx={{ mb: 2, borderColor: 'primary.main' }}>
                          <CardContent>
                            <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                              {sol.issue}
                            </Typography>
                            <Typography variant="body2" paragraph>
                              <strong>Kezelés:</strong> {sol.treatment}
                            </Typography>
                            {sol.products && sol.products.length > 0 && (
                              <Box mb={1}>
                                <Typography variant="subtitle2">Ajánlott termékek:</Typography>
                                <Box display="flex" flexWrap="wrap" gap={0.5}>
                                  {sol.products.map((product, i) => (
                                    <Chip key={i} label={product} size="small" color="primary" variant="outlined" />
                                  ))}
                                </Box>
                              </Box>
                            )}
                            <Typography variant="body2">
                              <strong>Alkalmazás:</strong> {sol.applicationMethod}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Gyakoriság:</strong> {sol.frequency}
                            </Typography>
                            <Typography variant="body2" color="success.main">
                              <strong>Várható eredmény:</strong> {sol.expectedResults}
                            </Typography>
                            {sol.warnings && sol.warnings.length > 0 && (
                              <Alert severity="warning" sx={{ mt: 1 }}>
                                {sol.warnings.map((w, i) => (
                                  <div key={i}>{w}</div>
                                ))}
                              </Alert>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Home Solutions */}
                {analysisResult.homeSolutions && analysisResult.homeSolutions.length > 0 && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <HomeIcon color="success" />
                        <Typography variant="h6">Házi Megoldások</Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {analysisResult.homeSolutions.map((sol, index) => (
                        <Card key={index} variant="outlined" sx={{ mb: 2, borderColor: 'success.main' }}>
                          <CardContent>
                            <Typography variant="subtitle1" fontWeight="bold" color="success.main" gutterBottom>
                              {sol.issue}
                            </Typography>
                            <Typography variant="body2" paragraph>
                              <strong>Kezelés:</strong> {sol.treatment}
                            </Typography>
                            {sol.ingredients && sol.ingredients.length > 0 && (
                              <Box mb={1}>
                                <Typography variant="subtitle2">Hozzávalók:</Typography>
                                <Box display="flex" flexWrap="wrap" gap={0.5}>
                                  {sol.ingredients.map((ing, i) => (
                                    <Chip key={i} label={ing} size="small" color="success" variant="outlined" />
                                  ))}
                                </Box>
                              </Box>
                            )}
                            <Typography variant="body2" paragraph>
                              <strong>Elkészítés:</strong> {sol.preparation}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Alkalmazás:</strong> {sol.applicationMethod}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Gyakoriság:</strong> {sol.frequency}
                            </Typography>
                            <Typography variant="body2" color="info.main">
                              <strong>Hatékonyság:</strong> {sol.effectiveness}
                            </Typography>
                            {sol.tips && sol.tips.length > 0 && (
                              <Box mt={1}>
                                <Typography variant="subtitle2">Tippek:</Typography>
                                <List dense>
                                  {sol.tips.map((tip, i) => (
                                    <ListItem key={i}>
                                      <ListItemIcon sx={{ minWidth: 32 }}>
                                        <TipsAndUpdatesIcon fontSize="small" color="success" />
                                      </ListItemIcon>
                                      <ListItemText primary={tip} />
                                    </ListItem>
                                  ))}
                                </List>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Prevention Tips */}
                {analysisResult.preventionTips && analysisResult.preventionTips.length > 0 && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CheckCircleIcon color="success" />
                        <Typography variant="h6">Megelőzési Tanácsok</Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <List>
                        {analysisResult.preventionTips.map((tip, index) => (
                          <ListItem key={index}>
                            <ListItemIcon>
                              <CheckCircleIcon color="success" />
                            </ListItemIcon>
                            <ListItemText primary={tip} />
                          </ListItem>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Care Recommendations */}
                {analysisResult.careRecommendations && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <GrassIcon color="primary" />
                        <Typography variant="h6">Gondozási Javaslatok</Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        {analysisResult.careRecommendations.watering && (
                          <Grid item xs={12} sm={6}>
                            <Card variant="outlined">
                              <CardContent>
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                  <WaterDropIcon color="info" />
                                  <Typography variant="subtitle1" fontWeight="bold">
                                    Öntözés
                                  </Typography>
                                </Box>
                                <Typography variant="body2">
                                  {analysisResult.careRecommendations.watering}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        )}
                        {analysisResult.careRecommendations.light && (
                          <Grid item xs={12} sm={6}>
                            <Card variant="outlined">
                              <CardContent>
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                  <WbSunnyIcon color="warning" />
                                  <Typography variant="subtitle1" fontWeight="bold">
                                    Fényigény
                                  </Typography>
                                </Box>
                                <Typography variant="body2">
                                  {analysisResult.careRecommendations.light}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        )}
                        {analysisResult.careRecommendations.temperature && (
                          <Grid item xs={12} sm={6}>
                            <Card variant="outlined">
                              <CardContent>
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                  <ThermostatIcon color="error" />
                                  <Typography variant="subtitle1" fontWeight="bold">
                                    Hőmérséklet
                                  </Typography>
                                </Box>
                                <Typography variant="body2">
                                  {analysisResult.careRecommendations.temperature}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        )}
                        {analysisResult.careRecommendations.humidity && (
                          <Grid item xs={12} sm={6}>
                            <Card variant="outlined">
                              <CardContent>
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                  <WaterDropIcon color="info" />
                                  <Typography variant="subtitle1" fontWeight="bold">
                                    Páratartalom
                                  </Typography>
                                </Box>
                                <Typography variant="body2">
                                  {analysisResult.careRecommendations.humidity}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        )}
                        {analysisResult.careRecommendations.fertilizing && (
                          <Grid item xs={12} sm={6}>
                            <Card variant="outlined">
                              <CardContent>
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                  <ScienceIcon color="success" />
                                  <Typography variant="subtitle1" fontWeight="bold">
                                    Tápanyag-utánpótlás
                                  </Typography>
                                </Box>
                                <Typography variant="body2">
                                  {analysisResult.careRecommendations.fertilizing}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        )}
                        {analysisResult.careRecommendations.soil && (
                          <Grid item xs={12} sm={6}>
                            <Card variant="outlined">
                              <CardContent>
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                  <GrassIcon color="primary" />
                                  <Typography variant="subtitle1" fontWeight="bold">
                                    Talaj
                                  </Typography>
                                </Box>
                                <Typography variant="body2">
                                  {analysisResult.careRecommendations.soil}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        )}
                        {analysisResult.careRecommendations.generalTips &&
                          analysisResult.careRecommendations.generalTips.length > 0 && (
                            <Grid item xs={12}>
                              <Card variant="outlined">
                                <CardContent>
                                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    Általános tippek
                                  </Typography>
                                  <List dense>
                                    {analysisResult.careRecommendations.generalTips.map((tip, i) => (
                                      <ListItem key={i}>
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                          <TipsAndUpdatesIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText primary={tip} />
                                      </ListItem>
                                    ))}
                                  </List>
                                </CardContent>
                              </Card>
                            </Grid>
                          )}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Prognosis */}
                {analysisResult.prognosis && (
                  <Paper elevation={0} sx={{ p: 2, mt: 2, backgroundColor: 'success.light', borderRadius: 2 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <HealingIcon />
                      <Typography variant="h6">Prognózis</Typography>
                    </Box>
                    <Typography variant="body1">{analysisResult.prognosis}</Typography>
                  </Paper>
                )}

                {/* Follow-up chat a növényorvossal */}
                <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <QuestionAnswerIcon color="primary" />
                    <Typography variant="h6">Kérdezz a növényorvostól</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    További kérdésed van az elemzéssel vagy a kezeléssel kapcsolatban?
                    Írd be ide!
                  </Typography>

                  {chatMessages.length > 0 && (
                    <Box
                      sx={{
                        maxHeight: 320,
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        my: 1.5,
                      }}
                    >
                      {chatMessages.map((msg, i) => (
                        <Box
                          key={i}
                          sx={{
                            alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                            maxWidth: '85%',
                          }}
                        >
                          <Paper
                            elevation={0}
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              backgroundColor: (theme) =>
                                msg.role === 'user'
                                  ? alpha(theme.palette.primary.main, 0.12)
                                  : theme.palette.action.hover,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                            >
                              {msg.content}
                            </Typography>
                          </Paper>
                        </Box>
                      ))}
                      {chatLoading && (
                        <Box sx={{ alignSelf: 'flex-start', p: 1 }}>
                          <CircularProgress size={20} />
                        </Box>
                      )}
                    </Box>
                  )}

                  <Box display="flex" gap={1} mt={1}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Pl. Milyen gyakran permetezzem a tejes permettel?"
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      disabled={chatLoading}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendChat();
                        }
                      }}
                      multiline
                      maxRows={4}
                    />
                    <IconButton
                      color="primary"
                      onClick={handleSendChat}
                      disabled={chatLoading || !chatInput.trim()}
                      sx={{ alignSelf: 'flex-end' }}
                    >
                      {chatLoading ? <CircularProgress size={24} /> : <SendIcon />}
                    </IconButton>
                  </Box>
                </Paper>
              </Box>
            </Box>
          )}
        </DialogContent>

        {/* Mentési visszajelzés */}
        {saveResult && (
          <Alert
            severity={saveResult.status === 'success' ? 'success' : 'error'}
            onClose={() => setSaveResult(null)}
            sx={{ mx: 2, mt: 1 }}
          >
            {saveResult.message}
            {saveResult.action === 'created_pending' && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                A növény az adminisztrátori jóváhagyás után jelenik meg a
                növénytárban.
              </Typography>
            )}
          </Alert>
        )}

        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button
            variant="contained"
            color="success"
            onClick={handleSaveToPlant}
            disabled={
              saving ||
              reanalyzing ||
              !analysisResult?.plantName?.latin ||
              saveResult?.status === 'success'
            }
            startIcon={
              saving ? (
                <CircularProgress size={20} />
              ) : saveResult?.status === 'success' ? (
                <CheckCircleIcon />
              ) : (
                <SaveIcon />
              )
            }
          >
            {saving
              ? 'Mentés...'
              : saveResult?.status === 'success'
                ? 'Elmentve'
                : 'Mentés növényhez'}
          </Button>
          <Button onClick={handleResultDialogClose} variant="outlined">
            Bezárás
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PlantAnalyzer;
